<template>
    <DashboardCard
        :icon="icon"
        :title="cardTitle"
        :show-footer="false"
        :show-error="showError"
        :on-reload-click="onReloadClick"
    >
        <template v-if="forAskNicelyStaff" #title-suffix>
            <img
                v-if="forAskNicelyStaff"
                class="role_staff_icon_sm"
                src="/img/icons/v3/spanner-purple.svg"
                alt="Only shown to Ask Nicely Staff"
            />
        </template>
        <template #content>
            <div v-if="loading" id="actions-panel">
                <ActionCardLoading v-for="i in cardsToShow" :key="i" />
            </div>
            <div
                v-else-if="actions.length > 0"
                id="actions-panel"
                data-testid="fm-dash-actions-panel"
            >
                <TransitionGroup name="actions">
                    <ActionCard
                        v-for="(action, index) in actions.slice(0, cardsToShow)"
                        :key="
                            action.slug ??
                            action.question_id ??
                            `action-${action.category}-${index}`
                        "
                        :action="action"
                        @trigger-action="emit('triggerAction', action, index)"
                        @dismiss-action="emit('dismissAction', action, index)"
                    />
                </TransitionGroup>
            </div>
            <div
                v-else
                id="recommended-actions-empty"
                data-testid="fm-dash-recommended-actions-empty"
            >
                {{
                    `Hoorah! You're all done${userName && ', ' + userName}! 🎉`
                }}
            </div>
        </template>
    </DashboardCard>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { faMessageCheck } from 'fontawesome/pro-regular-svg-icons'
import { faWrench } from 'fontawesome/free-solid-svg-icons'
import DashboardCard from '@/components/FrontlineManagerDash/DashboardCard.vue'
import ActionCard from '@/components/FrontlineManagerDash/ActionCard.vue'
import { RecommendedAction } from '@/entities/frontlineManagerDash'

import { IconDefinition } from 'fontawesome/free-solid-svg-icons'
import ActionCardLoading from '@/components/FrontlineManagerDash/ActionCardLoading.vue'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'

interface Props {
    actions: RecommendedAction[]
    title: string
    userName: string
    loading: boolean
    icon?: IconDefinition
    cardsToShow?: number
    showError?: boolean
    forAskNicelyStaff?: boolean
    onReloadClick?: Function
}
const props = withDefaults(defineProps<Props>(), {
    icon: faMessageCheck,
    cardsToShow: 3,
    onReloadClick: () => ({}),
})

const emit = defineEmits(['triggerAction', 'dismissAction'])

const cardTitle = computed(() => {
    if (props.loading) {
        return props.title
    }
    return `${props.title} (${props.actions?.length ?? 0})`
})
const spannerIcon = faWrench
</script>

<style lang="less" scoped>
@import '~@/styles/rain/variables.less';
@import '~@/styles/prime-vue/variables.less';

#actions-panel {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: @spacing-lg;
    padding-top: @spacing-xs;
}

#recommended-actions-empty {
    height: 180px;
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;
    font-size: @fontSize-lg;
    font-weight: 600;
    line-height: 150%;
    color: var(--p-gray-900);
}

.actions-move,
.actions-enter-active,
.actions-leave-active {
    transition: all 0.5s ease;
}
.actions-enter-from,
.actions-leave-to {
    opacity: 0;
    transform: translateX(30px);
}
.actions-leave-active {
    display: none;
}

.role_staff_icon_sm {
    width: 16px;
    height: 16px;
    margin-left: 6px;
    vertical-align: middle;
}

@media (min-width: @tabletBreakpoint) {
    #actions-panel {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: @spacing-xl;
        padding-top: 0;
    }
}

@media (min-width: @desktopBreakpoint) {
    #actions-panel {
        grid-template-columns: repeat(3, 1fr);
        gap: @spacing-3xl;
    }
}
</style>
